import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { SETTINGS_KEYS } from '../../config/environment'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { settingsService } from '../../services/settings.service'
import { toastService } from '../../services/toast.service'
import { uploadService } from '../../services/upload.service'
import { useAppStore } from '../../store/useAppStore'
import { captureOrganizationScope, showErrorIfSameOrganization } from '../../utils/orgScopedNotification'
import { getEffectiveAllowedMimes, isUploadAllowed } from '../../utils/uploadMimePolicy'
import './FileDrop.scss'

interface FileSystemEntry {
  isFile: boolean
  isDirectory: boolean
  name: string
  fullPath: string
}

interface FileSystemFileEntry extends FileSystemEntry {
  file(resolve: (file: File) => void, reject: (error: Error) => void): void
}

interface FileSystemDirectoryEntry extends FileSystemEntry {
  createReader(): FileSystemDirectoryReader
}

interface FileSystemDirectoryReader {
  readEntries(successCallback: (entries: FileSystemEntry[]) => void, errorCallback: (error: Error) => void): void
}

export const FileDrop: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [fileIsOver, setFileIsOver] = useState(false)
  const [hidden, setHidden] = useState(true)
  const fileLeaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // OS file-drags never fire dragstart on the page; in-app image/reorder drags always do.
  const internalDragRef = useRef(false)

  const dragDropEnabled = permissionsService.currentUserCan(PermissionAction.Add, PermissionType.Document)

  const uiSettings = useAppStore((s) => s.uiSettings)
  const supportedUploadTypes = uiSettings?.settings?.supported_upload_types
  const effectiveMimeSet = useMemo(() => {
    const allowed = uiSettings?.settings?.allowed_upload_mime_types
    return new Set(getEffectiveAllowedMimes(supportedUploadTypes, allowed))
  }, [uiSettings, supportedUploadTypes])

  const traverseFileTree = useCallback((entry: FileSystemEntry): Promise<File[]> => {
    if ((entry as FileSystemFileEntry).isFile) {
      return new Promise<File>((resolve, reject) => {
        ;(entry as FileSystemFileEntry).file(resolve, reject)
      }).then((file: File) => [file])
    }

    if ((entry as FileSystemDirectoryEntry).isDirectory) {
      return new Promise<File[]>((resolve, reject) => {
        const dirReader = (entry as FileSystemDirectoryEntry).createReader()
        const allEntries: FileSystemEntry[] = []

        const readEntries = () => {
          dirReader.readEntries((batch: FileSystemEntry[]) => {
            if (batch.length === 0) {
              const promises = allEntries.map((child) => traverseFileTree(child))
              Promise.all(promises)
                .then((results) => resolve(results.flat()))
                .catch(reject)
            } else {
              allEntries.push(...batch)
              readEntries()
            }
          }, reject)
        }

        readEntries()
      })
    }

    return Promise.resolve([])
  }, [])

  const handleDragLeave = useCallback(
    (event: DragEvent | null, immediate: boolean = false) => {
      if (!dragDropEnabled) return
      event?.preventDefault()
      event?.stopImmediatePropagation()

      const ms = immediate ? 0 : 500

      if (fileLeaveTimeoutRef.current) {
        clearTimeout(fileLeaveTimeoutRef.current)
      }

      fileLeaveTimeoutRef.current = setTimeout(() => {
        setFileIsOver(false)
        setTimeout(() => {
          setHidden(true)
        }, 150)
      }, ms)
    },
    [dragDropEnabled]
  )

  const handleDragStart = useCallback(() => {
    internalDragRef.current = true
  }, [])

  const handleDragEnd = useCallback(() => {
    internalDragRef.current = false
    handleDragLeave(null, true)
  }, [handleDragLeave])

  const handleDragOver = useCallback(
    (event: DragEvent) => {
      if (!dragDropEnabled || internalDragRef.current || !event.dataTransfer?.types?.includes('Files')) {
        return
      }
      event.preventDefault()
      event.stopImmediatePropagation()

      setTimeout(() => {
        setFileIsOver(true)
      }, 1)
      setHidden(false)

      if (fileLeaveTimeoutRef.current) {
        clearTimeout(fileLeaveTimeoutRef.current)
        fileLeaveTimeoutRef.current = null
      }
    },
    [dragDropEnabled]
  )

  const handleDrop = useCallback(
    (event: DragEvent) => {
      if (!dragDropEnabled) return
      event.preventDefault()
      event.stopImmediatePropagation()

      if (internalDragRef.current) {
        internalDragRef.current = false
        handleDragLeave(event, true)
        return
      }

      const files: File[] = []
      const entries: FileSystemEntry[] = []

      if (event.dataTransfer?.items && event.dataTransfer.items.length) {
        for (const item of Array.from(event.dataTransfer.items)) {
          const entry = (item as any).webkitGetAsEntry?.()
          if (entry) {
            entries.push(entry)
          } else if (item.kind === 'file') {
            const file = item.getAsFile()
            if (file) files.push(file)
          }
        }
      } else if (event.dataTransfer?.files) {
        for (const file of Array.from(event.dataTransfer.files)) {
          files.push(file)
        }
      }

      const uploadFiltered = (allFiles: File[]) => {
        const allowed = allFiles.filter((f) => isUploadAllowed(f, effectiveMimeSet, supportedUploadTypes))
        const rejected = allFiles.length - allowed.length
        if (rejected > 0) {
          toastService.showError(t('files_rejected_by_policy', { count: rejected }))
        }
        if (allowed.length === 0) return
        toastService.showInfo(t('initiating_upload'), 3000)
        const suppressOnDashboard = settingsService.get(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_SUPPRESS_ON_DASHBOARD)
        navigate(suppressOnDashboard ? '/tasks?tab=completed' : '/dashboard')
        const uploadOrgId = captureOrganizationScope()
        allowed.forEach((file) => {
          uploadService.uploadDocument(file).catch((error) => {
            showErrorIfSameOrganization(uploadOrgId, `${t('error_uploading_document')} "${file.name}": ${error.message || error}`, error)
          })
        })
      }

      if (entries.length) {
        const promises = entries.map((entry) => traverseFileTree(entry))
        Promise.all(promises)
          .then((results) => {
            files.push(...results.flat())
            uploadFiltered(files)
          })
          .catch((e) => {
            toastService.showError(`${t('failed_to_read_dropped_items')} ${e.message || e}`)
          })
      } else if (files.length) {
        uploadFiltered(files)
      }

      handleDragLeave(event, true)
    },
    [dragDropEnabled, t, traverseFileTree, handleDragLeave, effectiveMimeSet, supportedUploadTypes, navigate]
  )

  const handleWindowBlur = useCallback(() => {
    if (fileIsOver) {
      handleDragLeave(null)
    }
  }, [fileIsOver, handleDragLeave])

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden && fileIsOver) {
      handleDragLeave(null)
    }
  }, [fileIsOver, handleDragLeave])

  useEffect(() => {
    if (!dragDropEnabled) return

    document.addEventListener('dragstart', handleDragStart)
    document.addEventListener('dragend', handleDragEnd)
    document.addEventListener('dragover', handleDragOver)
    document.addEventListener('dragleave', handleDragLeave)
    document.addEventListener('drop', handleDrop)
    window.addEventListener('blur', handleWindowBlur)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('dragstart', handleDragStart)
      document.removeEventListener('dragend', handleDragEnd)
      document.removeEventListener('dragover', handleDragOver)
      document.removeEventListener('dragleave', handleDragLeave)
      document.removeEventListener('drop', handleDrop)
      window.removeEventListener('blur', handleWindowBlur)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (fileLeaveTimeoutRef.current) {
        clearTimeout(fileLeaveTimeoutRef.current)
      }
    }
  }, [dragDropEnabled, handleDragStart, handleDragEnd, handleDragOver, handleDragLeave, handleDrop, handleWindowBlur, handleVisibilityChange])

  return (
    <>
      <div className={fileIsOver ? 'pe-none' : ''}>{children}</div>

      <div
        className={`global-dropzone-overlay position-fixed top-0 start-0 bottom-0 end-0 text-center pe-none ${fileIsOver && !hidden ? 'active' : ''}`}
      >
        <h2 className="pe-none position-absolute top-50 start-50 translate-middle">{t('drop_files_to_begin_upload')}</h2>
      </div>
    </>
  )
}
